import React, { useCallback, useEffect, useState } from 'react';
import { Filter, FunctionField, TextField, useDataProvider, useNotify } from 'react-admin';

import makeStyles from '@material-ui/core/styles/makeStyles';

import { Actions, Datagrid, List } from 'components/list';
import { ActionField } from 'components/fields';
import SearchInput from 'components/list/SearchInput';

import NameField from '../NameField';
import DeleteRewardBenefitField from './DeleteRewardBenefitField';
import EditBenefitActionField from './EditBenefitActionField';
import AddBenefitButton from './AddBenefitButton';
import SwitchField from './SwitchField';
import { RewardBenefitDto, RewardLevelDto } from '../../types';

const useStyles = makeStyles((theme) => ({
  form: {
    justifyContent: 'flex-end',
    width: '100%',
    '& .filter-field': {
      width: '50%',
    },
  },
}));

const BenefitsFilter = (props) => {
  const classes = useStyles();

  return (
    <Filter classes={classes} {...props}>
      <SearchInput label="Search" source="search" alwaysOn onlySearch loading={props.loading} />
    </Filter>
  );
};

const BenefitActions = (props) => (
  <Actions {...props}>
    <AddBenefitButton />
  </Actions>
);

const Benefits = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [levels, setLevels] = useState<RewardLevelDto[]>([]);
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const [benefitLevelsById, setBenefitLevelsById] = useState<Record<string, string[]>>({});

  const handleBenefitSaved = useCallback((benefitId: string, levels: string[]) => {
    if (!benefitId) {
      return;
    }

    setBenefitLevelsById((prev) => ({
      ...prev,
      [benefitId]: (levels ?? []).filter((id): id is string => Boolean(id)),
    }));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchLevels = async () => {
      try {
        const { data } = await dataProvider.getList<RewardLevelDto>('rewardsLevel', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
        });

        if (!isMounted) {
          return;
        }

        setLevels(Array.isArray(data) ? data : []);
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        const message = error?.message || 'Failed to load reward levels';
        notify(message, 'warning');
      }
    };

    fetchLevels();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify]);

  const handleToggleLevel = async (benefit: RewardBenefitDto, levelId?: RewardLevelDto['id']) => {
    if (!benefit?.id || !levelId) {
      return;
    }

    const benefitId = benefit.id;
    const key = `${benefitId}-${levelId}`;

    const hasOverride = Object.prototype.hasOwnProperty.call(benefitLevelsById, benefitId);
    const currentLevels = (
      (hasOverride ? benefitLevelsById[benefitId] : benefit.levels) ?? []
    ).filter((id): id is string => Boolean(id));
    const isActive = currentLevels.includes(levelId);
    const nextLevels = isActive
      ? currentLevels.filter((id) => id !== levelId)
      : Array.from(new Set([...currentLevels, levelId]));

    setBenefitLevelsById((prev) => ({ ...prev, [benefitId]: nextLevels }));
    setUpdating((prev) => ({ ...prev, [key]: true }));

    try {
      if (isActive) {
        await dataProvider.delete('rewardsUpdateBenefitLevels', {
          levelId,
          benefitId,
        });
      } else {
        await dataProvider.post('rewardsUpdateBenefitLevels', {
          levelId,
          benefitId,
          data: {},
        });
      }
    } catch (error: any) {
      setBenefitLevelsById((prev) => {
        if (!hasOverride) {
          const next = { ...prev };
          delete next[benefitId];
          return next;
        }

        return { ...prev, [benefitId]: currentLevels };
      });

      const message = error?.message || 'Failed to update perk levels';
      notify(message, 'warning');
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <List
      {...props}
      resource="rewardsBenefit"
      basePath="/rewardsSettings"
      actions={<BenefitActions />}
      filters={<BenefitsFilter />}>
      <Datagrid {...props}>
        <NameField source="title" imageSource="iconUrl" label="Perk" />
        <TextField source="systemType" label="System integration" sortable={false} />
        {levels.map((level) => (
          <FunctionField
            key={level.id ?? level.name}
            label={level.name}
            sortable={false}
            render={(record: RewardBenefitDto) => (
              <SwitchField
                benefit={
                  record?.id && Object.prototype.hasOwnProperty.call(benefitLevelsById, record.id)
                    ? { ...record, levels: benefitLevelsById[record.id] }
                    : record
                }
                levelId={level.id}
                loading={updating[`${record.id}-${level.id}`]}
                onToggle={handleToggleLevel}
              />
            )}
          />
        ))}
        <ActionField benefitLevelsById={benefitLevelsById} onBenefitSaved={handleBenefitSaved}>
          <EditBenefitActionField />
          <DeleteRewardBenefitField />
        </ActionField>
      </Datagrid>
    </List>
  );
};

export default Benefits;
